import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2, PackageSearch } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * inFlow product typeahead. Read-only: searches the live inFlow catalogue and lets the
 * rep pick a real product (guaranteeing a valid productId). Never creates products.
 */
export function InflowProductPicker({ item, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const search = useQuery({
    queryKey: ["inflow-products", q],
    queryFn: async () => (await client.get("/inflow/products", { params: { q } })).data,
    enabled: open,
    staleTime: 30_000,
  });

  const label = item.inflow_product_name || item.name || "";
  const mapped = !!item.inflow_product_id;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid={`product-picker-${item.id}`}
          className={cn("w-full justify-between font-normal", !mapped && "border-warning/50")}
        >
          <span className="flex min-w-0 items-center gap-2">
            {mapped
              ? <Check className="h-3.5 w-3.5 shrink-0 text-success" />
              : <PackageSearch className="h-3.5 w-3.5 shrink-0 text-warning" />}
            <span className="truncate">{label || "Select inFlow product…"}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search inFlow products…" value={q} onValueChange={setQ} data-testid="product-search-input" />
          <CommandList>
            {search.isLoading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            ) : (
              <>
                <CommandEmpty>No inFlow products found.</CommandEmpty>
                {(search.data || []).map((p) => (
                  <CommandItem
                    key={p.product_id}
                    value={p.product_id}
                    data-testid={`product-option-${p.product_id}`}
                    onSelect={() => {
                      onSelect({
                        inflow_product_id: p.product_id,
                        inflow_product_name: p.name,
                        name: p.name,
                        sku: p.sku || null,
                      });
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate">{p.name}</span>
                      {p.sku && <span className="shrink-0 text-xs text-muted-foreground">{p.sku}</span>}
                    </div>
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
